import { useMemo } from "react";
import type { ChWireQuestion } from "./caseHistoryWireTypes";
import { isPanel, wireChildElements, wireName, wireTitle } from "./caseHistoryWireTypes";
import { CaseHistoryWireElements } from "./CaseHistoryWireElements";

export interface ParsedWirePage {
  name: string;
  title: string;
  sections: { name: string; title: string; elements: ChWireQuestion[] }[];
}

export interface ParsedWireDocument {
  title: string;
  pages: ParsedWirePage[];
}

/** Shared parser for portal config JSON (`pages` → panel sections). */
export function parseWireDocument(doc: Record<string, unknown>): ParsedWireDocument {
  const title =
    typeof doc.title === "string"
      ? doc.title
      : typeof doc.Title === "string"
        ? doc.Title
        : "";
  const pagesRaw = Array.isArray(doc.pages)
    ? doc.pages
    : Array.isArray(doc.Pages)
      ? doc.Pages
      : [];
  const pages: ParsedWirePage[] = [];

  for (const p of pagesRaw) {
    if (!p || typeof p !== "object") continue;
    const pr = p as Record<string, unknown>;
    const name =
      typeof pr.name === "string" ? pr.name : typeof pr.Name === "string" ? pr.Name : "page";
    const pageTitle =
      typeof pr.title === "string"
        ? pr.title
        : typeof pr.Title === "string"
          ? pr.Title
          : name;
    const elements = Array.isArray(pr.elements)
      ? pr.elements
      : Array.isArray(pr.Elements)
        ? pr.Elements
        : [];
    const sections: ParsedWirePage["sections"] = [];
    let pendingNonPanels: ChWireQuestion[] = [];

    const flushPending = () => {
      if (pendingNonPanels.length === 0) return;
      sections.push({
        name: `_inline_${sections.length}`,
        title: "",
        elements: pendingNonPanels,
      });
      pendingNonPanels = [];
    };

    for (const el of elements) {
      if (!el || typeof el !== "object") continue;
      const node = el as ChWireQuestion;
      if (isPanel(node)) {
        flushPending();
        sections.push({
          name: wireName(node) || `panel_${sections.length}`,
          title: wireTitle(node),
          elements: wireChildElements(node),
        });
      } else {
        pendingNonPanels.push(node);
      }
    }
    flushPending();

    if (sections.length > 0) {
      pages.push({ name, title: pageTitle, sections });
    }
  }

  return { title, pages };
}

function PageBlock({ page }: { page: ParsedWirePage }) {
  return (
    <div className="mb-2">
      {page.title ? (
        <h2 className="mb-4 text-lg font-semibold text-gray-900">{page.title}</h2>
      ) : null}
      {page.sections.map((section, sIdx) => (
        <div key={`${section.name}-${sIdx}`} className="mb-8">
          {section.title ? (
            <h3 className="mb-3 text-base font-medium text-gray-800">{section.title}</h3>
          ) : null}
          <CaseHistoryWireElements elements={section.elements} />
        </div>
      ))}
    </div>
  );
}

export function CaseHistoryWireInteractive({
  document,
  pageIndex,
}: {
  document: Record<string, unknown>;
  /** If set, only this page (0-based) is rendered. */
  pageIndex?: number;
}) {
  const parsed = useMemo(() => parseWireDocument(document), [document]);

  if (parsed.pages.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-gray-600">
        No sections found in this questionnaire. Add pages and panels in the editor.
      </p>
    );
  }

  if (pageIndex !== undefined) {
    const page = parsed.pages[pageIndex];
    if (!page) {
      return (
        <p className="py-6 text-center text-sm text-gray-600">This page does not exist.</p>
      );
    }
    return (
      <div className="case-history-wire-preview text-gray-900">
        <PageBlock page={page} />
      </div>
    );
  }

  return (
    <div className="case-history-wire-preview text-gray-900">
      {parsed.title ? (
        <h1 className="mb-6 text-xl font-semibold text-gray-900 sm:text-2xl">{parsed.title}</h1>
      ) : null}
      {parsed.pages.map((page) => (
        <div key={page.name} className="mb-10">
          <PageBlock page={page} />
        </div>
      ))}
    </div>
  );
}
